import { Box, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button, Icon } from '@verantos-dev/shared-components';

import { CompleteSupervisionModal } from './components/CompleteSupervisionModal';
import { SupervisorReviewTabSelection } from './components/SupervisorReviewTabSelection';
import { SupervisorSelectedTab } from './components/SupervisorSelectedTab';
import { useReviewAnnotationsSidebar } from './useReviewAnnotationsSidebar';
import { useSupervisorMode } from '../../context/SupervisorModeContext';

export const ReviewAnnotationsSidebar = ({ rightWidth }: { rightWidth: number }) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { isReadOnly } = useSupervisorMode();
  const {
    selectedNoteId,
    showAnnotations,
    handleShowAnnotations,
    showMarkTaskCompleteModal,
    setShowMarkTaskCompleteModal,
    disableMarkCompleteButton,
  } = useReviewAnnotationsSidebar();
  if (!selectedNoteId) {
    return null;
  }
  return (
    <Box sx={{ width: rightWidth }}>
      {!showAnnotations ? (
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(4),
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: theme.spacing(10),
            px: theme.spacing(6),
          }}
        >
          <Button
            id="annotation-start-annotation-button"
            displayLabel={formatMessage({
              id: 'supervisorContent.noteSection.showAnnotationButton',
            })}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              fontSize: '15px',
              textTransform: 'none',
            }}
            disabled={selectedNoteId == null}
            onClickHandler={() => handleShowAnnotations(true)}
          />
        </Box>
      ) : (
        <Box
          id="header-review-annotations-cards"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 48px)', // Account for container padding (24px top + 24px bottom)
            maxHeight: 'calc(100vh - 48px)',
            borderLeft: `1px solid ${theme.palette.divider}`,
            p: theme.spacing(4),
          }}
        >
          <Box
            id="header"
            sx={{
              flex: '1 1 auto',
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                  <Typography variant="h5">
                    {formatMessage({ id: 'workspaceCommon.annotationConfig.title' })}
                  </Typography>
                </Box>
                <IconButton
                  id="annotation-config-close-icon-button"
                  onClick={() => {
                    handleShowAnnotations(false);
                  }}
                >
                  <Icon
                    id="supervisor-review-close-show-annotations-icon"
                    icon="clear"
                    width={16}
                  />
                </IconButton>
              </Box>
              <Divider />
              <Box
                id="supervisor-review-tab-and-content"
                sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}
              >
                <SupervisorReviewTabSelection />
                <SupervisorSelectedTab />
              </Box>
            </Box>
          </Box>
          {!isReadOnly && (
            <Box
              id="mark-as-completed-button-box"
              sx={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                py: theme.spacing(4),
                px: theme.spacing(2),
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                id="mark-as-completed"
                displayLabel={formatMessage({ id: 'supervisorReview.markAsCompleted.button' })}
                disabled={disableMarkCompleteButton}
                onClickHandler={() => setShowMarkTaskCompleteModal(true)}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  fontSize: '15px',
                  textTransform: 'none',
                  width: '100%',
                }}
              />
            </Box>
          )}
        </Box>
      )}
      <CompleteSupervisionModal
        showModal={showMarkTaskCompleteModal}
        setShowModal={setShowMarkTaskCompleteModal}
      />
    </Box>
  );
};
